import CircleWithLockIcon from "@/assets/colorable/circle_with_lock.svg?react";
import InfoIcon from "@/assets/colorable/info.svg?react";
import type {PackageSubscriptionSerializer} from "@/generated/DjangoTypes";
import {usePlanPerks} from "@/hooks/usePlanPerks";
import clsxm from "@/lib/clsxm";
import useUserStore from "@/store/user";
import map from "lodash/map";
import omit from "lodash/omit";
import {type FC} from "react";
import {useTranslation} from "react-i18next";
import {Tooltip} from "react-tooltip";

const LockedFeaturesList: FC<{
  addOnsListClassName?: string;
  className?: string;
  iconClassName?: string;
  labelClassName?: string;
  subscription?: PackageSubscriptionSerializer | null;
}> = ({addOnsListClassName, className, iconClassName, labelClassName, subscription: subscriptionFromProps}) => {
  const {t} = useTranslation(["common", "plans"]);
  const subscriptionFromStore = useUserStore(state => state.user.package_subscription);

  const subscription = subscriptionFromProps ?? subscriptionFromStore;
  const {lockedFeatures} = usePlanPerks(subscription);
  const hasTiktokSearch = !!subscription?.tiktok_search;
  const hasCampaignBooster = !!subscription?.campaign_booster;

  const lockedChips = omit(lockedFeatures, ["invitations_per_campaign"]);
  const hasLockedAddOn = !hasTiktokSearch || !hasCampaignBooster;
  const hasLockedChips = Object.keys(lockedChips).length > 0;

  if (!hasLockedChips && !hasLockedAddOn) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      {hasLockedChips && (
        <label
          htmlFor="lockedFeatures"
          className={clsxm("text-neutral-grey-dark m-0 text-sm font-medium", labelClassName)}>
          {t("lockedFeatures")}
        </label>
      )}
      <div id="lockedFeatures" className={clsxm("flex flex-wrap gap-5 gap-y-3", className)}>
        {map(lockedChips, (value, key) => (
          <div key={key} className="flex w-fit flex-row items-center gap-1.5">
            <CircleWithLockIcon className={clsxm("text-neutral-grey-dark mr-1.5 size-6 shrink-0", iconClassName)} />
            <span className="text-neutral-black font-semibold">{value}</span>
            <span className="text-neutral-black font-semibold whitespace-nowrap">
              {t(key as "influencers_per_campaign")}
            </span>
          </div>
        ))}
        {hasLockedAddOn && (
          <>
            <div className="bg-neutral-grey-light-dark flex h-px w-full" />
            <div className="flex flex-col gap-2">
              <span className="text-neutral-grey-dark text-sm font-medium">{t("plans:locked_add_ons")}</span>
              <div className={clsxm("flex flex-wrap gap-5 gap-y-3", addOnsListClassName)}>
                {!hasTiktokSearch && (
                  <div className="flex w-fit flex-row items-center gap-1.5">
                    <CircleWithLockIcon
                      className={clsxm("text-neutral-grey-dark mr-1.5 size-6 shrink-0", iconClassName)}
                    />
                    <span className="text-neutral-black font-semibold whitespace-nowrap">
                      {t("plans:add_on_social_search_title")}
                    </span>
                    <InfoIcon
                      className="text-primary-dark-solid size-4 shrink-0 cursor-pointer"
                      data-tooltip-id="locked-add-on-info"
                      data-tooltip-content={t("plans:add_on_social_search_info")}
                    />
                  </div>
                )}
                {!hasCampaignBooster && (
                  <div className="flex w-fit flex-row items-center gap-1.5">
                    <CircleWithLockIcon
                      className={clsxm("text-neutral-grey-dark mr-1.5 size-6 shrink-0", iconClassName)}
                    />
                    <span className="text-neutral-black font-semibold whitespace-nowrap">
                      {t("plans:add_on_booster_title")}
                    </span>
                    <InfoIcon
                      className="text-primary-dark-solid size-4 shrink-0 cursor-pointer"
                      data-tooltip-id="locked-add-on-info"
                      data-tooltip-content={t("plans:add_on_booster_info")}
                    />
                  </div>
                )}
                <Tooltip id="locked-add-on-info" place="top" variant="dark" className="z-100 max-w-64 text-xs" />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LockedFeaturesList;
